import moment from 'moment';

/**
 * Datenschicht der Druckansicht: aus den Store-Slices werden flache Zeilen, die sowohl die
 * gedruckte Tabelle als auch der CSV-Export benutzen. Bewusst frei von React und vom Store -
 * das ist der Teil mit den Fallunterscheidungen und deshalb der Teil, der Tests bekommt.
 *
 * Die beiden Quellen sehen unterschiedlich aus: erfasste Teilnahmen tragen echte Booleans,
 * die vom Server geholten Meldungen 0/1 (siehe Schema "Punktemeldungen" in
 * openapi-veranstalter-api.yaml). Hier wird das einmal geradegezogen.
 */

const KEINE_BESCHRIFTUNG = { text: "" };

const beschriftungZu = (beschriftungen, efn) => beschriftungen[efn] || KEINE_BESCHRIFTUNG;

const alsFlag = (wert) => wert === true || wert === 1 || wert === "1";

const alsDatum = (wert) => wert ? moment(wert).format('DD.MM.YYYY') : "";

const alsZeitpunkt = (wert) => wert ? moment(wert).format('DD.MM.YYYY HH:mm') : "";

/**
 * Anfang und Ende, Mitte verdeckt: 80276060…5921. Auf der Anwesenheitsliste genuegt das - der
 * Unterschreibende erkennt seine Zeile, der Nachbar liest keine vollstaendige fremde EFN von
 * einem Blatt, das offen auf dem Einlasstisch liegt. Im Meldeprotokoll steht die volle EFN,
 * das ist das Archivdokument.
 *
 * Geschnitten wird bei 8, weil dort die KID endet: der sichtbare Anfang liest sich damit als
 * "80 Arzt | 276 Deutschland | 060 Kammer" statt mitten in der KID abzubrechen. Innerhalb
 * einer Kammer ist dieser Teil bei allen gleich und traegt nichts zur Unterscheidung bei -
 * seinen Wert hat er bei Veranstaltungen mit Teilnehmenden aus mehreren Kammern, wo die
 * abweichende KID sofort auffaellt. Hinten bleiben dieselben vier Stellen wie zuvor, damit
 * niemand weniger wiedererkennt als vorher.
 */
const SICHTBAR_VORN = 8;
const SICHTBAR_HINTEN = 4;

export const kuerzeEFN = (efn) => {
	const text = typeof efn === 'string' ? efn : "";
	if(text.length <= SICHTBAR_VORN + SICHTBAR_HINTEN) return text;
	return `${text.slice(0, SICHTBAR_VORN)}…${text.slice(-SICHTBAR_HINTEN)}`;
};

/**
 * Anreden und Titel am Anfang zaehlen fuer die Sortierung nicht mit. Die Teilnehmer-App der
 * LAEKH legt ihre Anrede als "Dr. med. Max Mustermann" in den QR-Code - ohne diesen Schritt
 * stuende am Einlasstisch die halbe Liste unter "D" und waere unbrauchbar.
 *
 * Umgestellt wird nichts: ob "Vorname Nachname" oder "Nachname, Vorname" gemeint ist, ist von
 * aussen nicht entscheidbar (gleiche Ueberlegung wie beim Parser der Barcode-Inhalte). Es
 * faellt nur weg, was sicher kein Name ist.
 */
const TITEL_ODER_ANREDE = /^(herr|frau|dr|prof|dipl|med|dent|vet|rer|nat|univ|habil|h\.?c|mag|ing)\.?$/i;

export const sortierSchluessel = (text) => {
	const worte = (text || "").trim().split(/\s+/).filter(wort => wort);
	let erstes = 0;
	while(erstes < worte.length - 1 && TITEL_ODER_ANREDE.test(worte[erstes])) erstes++;
	return worte.slice(erstes).join(" ");
};

/**
 * Alphabetisch nach Name/Notiz. Die Bildschirmlisten sortieren nach Erfassungszeit - am
 * Einlasstisch sucht aber niemand nach dem Zeitpunkt, sondern nach dem Namen. Zeilen ohne
 * Namen koennen nicht einsortiert werden und wandern ans Ende, dort nach EFN.
 */
export const sortiereFuerDruck = (zeilen) => [...zeilen].sort((eine, andere) => {
	const einText = sortierSchluessel(eine.text);
	const andererText = sortierSchluessel(andere.text);
	if(einText && !andererText) return -1;
	if(!einText && andererText) return 1;
	if(einText && andererText){
		const vergleich = einText.localeCompare(andererText, 'de', { sensitivity: 'base' });
		if(vergleich !== 0) return vergleich;
	}
	return (eine.efn || "").localeCompare(andere.efn || "");
});

const zeileAusTeilnahme = (teilnahme, beschriftungen) => {
	const beschriftung = beschriftungZu(beschriftungen, teilnahme.efn);
	return {
		efn: teilnahme.efn,
		text: beschriftung.text || "",
		punkteBasis: alsFlag(teilnahme.punkte_basis_flag),
		punkteLernerfolg: alsFlag(teilnahme.punkte_lernerfolg_flag),
		punkteReferent: Number(teilnahme.punkte_referent) || 0,
		teilnahmedatum: alsDatum(teilnahme.teilnahmedatum),
		meldedatum: alsZeitpunkt(teilnahme.last_modified)
	};
};

/**
 * Die Anschrift der Tagungsstaette, so wie die anerkennende Kammer sie gepflegt hat. Alle
 * Felder sind optional - eine Kammer, die nur ort und plz fuehrt, ergibt zwei Zeilen, eine
 * Kammer, die gar nichts fuehrt, keine einzige. Deshalb wird gefiltert und nicht formatiert.
 *
 * fk_staat bleibt bewusst aussen vor: das ist eine Zahl aus TR 4.4 (0 = Deutschland), und
 * ihren Klartext haette der Client nur ueber einen zusaetzlichen Aufruf der Referenzdaten -
 * ein Netzzugriff genau dort, wo die Druckansicht sonst ohne Netz auskommt. Fuer die
 * Veranstaltungen, um die es hier geht, ist der Staat ohnehin Deutschland.
 */
export const baueVeranstaltungsort = (veranstaltung = {}) => {
	const { staette_zeile_1, staette_zeile_2, staette_strasse, staette_ortsteil, plz, ort } = veranstaltung;
	const ortszeile = [plz, ort].map(teil => (teil || "").trim()).filter(teil => teil).join(" ");
	return [staette_zeile_1, staette_zeile_2, staette_strasse, staette_ortsteil, ortszeile]
		.map(zeile => (zeile || "").trim())
		.filter(zeile => zeile);
};

export const baueAnwesenheitsliste = (erfassteTeilnahmenOfVNR = {}, beschriftungenOfVNR = {}) =>
	sortiereFuerDruck(Object.values(erfassteTeilnahmenOfVNR || {})
		.map(teilnahme => zeileAusTeilnahme(teilnahme, beschriftungenOfVNR || {})));

/**
 * Ungefiltert und ohne Zusammenfassen mehrerer Meldungen zu einer EFN: eine Korrektur gehoert
 * in ein Protokoll hinein, nicht wegsortiert.
 */
export const baueProtokollliste = (teilnahmeMeldungenOfVNR = {}, beschriftungenOfVNR = {}) =>
	sortiereFuerDruck(Object.values(teilnahmeMeldungenOfVNR || {})
		.map(meldung => zeileAusTeilnahme(meldung, beschriftungenOfVNR || {})));

/**
 * Wieviele Zeilen keinen Namen tragen. Im Protokoll ist das der Normalfall, sobald mit
 * mehreren Geraeten erfasst wurde: die Meldungen kommen vom Server, die Namen liegen nur
 * auf dem Geraet, das sie erfasst hat.
 */
export const zaehleOhneBeschriftung = (zeilen) =>
	zeilen.filter(zeile => !(zeile.text || "").trim()).length;

const maskiereCsvFeld = (wert) => `"${String(wert === null || wert === undefined ? "" : wert).replace(/"/g, '""')}"`;

/**
 * Semikolon und CRLF, damit Excel die Datei ohne Importdialog richtig zerlegt; das BOM setzt
 * erst der Download (siehe Druckansicht.jsx), damit die Umlaute stimmen.
 *
 * Die EFN wird als gewoehnliches Textfeld geschrieben und nicht als ="802...". Excel zeigt sie
 * dann in der Standardformatierung als 8,02761E+14 - der Preis dafuer, dass in dieser Datei
 * kein Feld mit = beginnt. Formeln in einer Datei, deren Namensspalte frei eingegeben wird,
 * waeren genau das Muster, vor dem CSV-Injection-Warnungen stehen.
 */
export const zuCsv = (zeilen, spalten) => {
	const kopf = spalten.map(spalte => maskiereCsvFeld(spalte.titel)).join(';');
	const inhalt = zeilen.map(zeile => spalten.map(spalte => maskiereCsvFeld(spalte.wert(zeile))).join(';'));
	return [kopf, ...inhalt].join('\r\n') + '\r\n';
};

export const alsJaNein = (flag) => flag ? "Ja" : "–";
